import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Dropdown from '@/components/ui/Dropdown';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import CustomTooltip from '@/components/ui/Tooltip/Tooltip';
import chevron from '@/assets/icons/chevron.svg';
import icFilterExpander from '@/assets/icons/filterExpander.svg';
import tooltipInfo from '@/assets/icons/tooltip-info.svg';
import filterStyles from '@/components/filters/OptionsRangeFilter/OptionsRangeFilter.module.css';
import fundStyles from '@/components/FundSelect/fundSelect.module.css';
import styles from '@/components/ui/SearchInput/SearchInput.module.css';

interface SupplierOption {
  id: number;
  name: string;
  taxId: string;
}

interface SupplierFilterProps {
  selectedSupplierId: number | null;
  onSupplierChange: (id: number | null) => void;
}

const SupplierFilter = ({ selectedSupplierId, onSupplierChange }: SupplierFilterProps) => {
  const { t } = useTranslation();
  const tooltipId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;
  const listContainerRef = useRef<HTMLDivElement>(null);

  const { data: allSuppliers, isLoading } = useGetAuthApi<SupplierOption[]>('management/suppliers');

  const filteredSuppliers = (allSuppliers ?? []).filter((s) =>
    `${s.name} ${s.taxId}`.toLowerCase().includes(searchValue.toLowerCase())
  );

  const virtualizer = useVirtualizer({
    count: filteredSuppliers.length,
    getScrollElement: () => listContainerRef.current,
    estimateSize: () => 40,
    overscan: 5,
  });

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSetOpen = (open: boolean) => {
    setIsOpen(open);
    if (!open) setSearchValue('');
  };

  const handleSelect = (supplier: SupplierOption) => {
    onSupplierChange(supplier.id);
    setIsOpen(false);
    setSearchValue('');
  };

  const handleClear = () => {
    onSupplierChange(null);
    setIsOpen(false);
    setSearchValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredSuppliers.length > 0) {
      handleSelect(filteredSuppliers[0]);
    }
  };

  const hasValue = selectedSupplierId != null;
  const selectedSupplier = selectedSupplierId != null
    ? (allSuppliers ?? []).find((s) => s.id === selectedSupplierId) ?? null
    : null;
  const triggerLabel = selectedSupplier ? selectedSupplier.name : t('supplier');

  return (
    <div className="flex items-center gap-1">
      <Dropdown
        trigger={
          <Button
            className={`
              ${filterStyles.filterButton}
              ${isOpen ? filterStyles.filterButtonDropdownOpen : ''}
              ${hasValue ? filterStyles.filterButtonHasValue : ''}
            `}
          >
            <span className="body-md-regular text-(--font-primary)">{triggerLabel}</span>
            <img src={icFilterExpander} alt="expander" />
          </Button>
        }
        contentAlign="start"
        isOpen={isOpen}
        setIsOpen={handleSetOpen}
      >
        <div className={fundStyles['select-menu']}>
          <SearchInput
            value={searchValue}
            onChange={setSearchValue}
            placeholder={t('searchSupplier')}
            className={fundStyles['search-container']}
            inputRef={searchInputRef}
            onKeyDown={handleKeyDown}
          />
          <div className={fundStyles['options-container']}>
            {hasValue && (
              <button className={fundStyles.option} onClick={handleClear}>
                <span className="body-md-regular">{t('allSuppliers')}</span>
              </button>
            )}
            {isLoading ? (
              <p className={`${fundStyles.option} body-sm-regular`}>{t('loading')}...</p>
            ) : filteredSuppliers.length === 0 ? (
              <p className={`${fundStyles.option} body-sm-regular`}>{t('noSuppliersFound')}</p>
            ) : (
              <div
                ref={listContainerRef}
                className={fundStyles['virtual-container']}
                style={{ height: Math.min(filteredSuppliers.length * 40, 220) }}
              >
                <div
                  className={fundStyles['virtual-content']}
                  style={{ height: `${virtualizer.getTotalSize()}px` }}
                >
                  {virtualizer.getVirtualItems().map((virtualRow) => {
                    const supplier = filteredSuppliers[virtualRow.index];
                    return (
                      <div
                        key={virtualRow.key}
                        ref={virtualizer.measureElement}
                        data-index={virtualRow.index}
                        className={fundStyles['virtual-item']}
                        style={{ transform: `translateY(${virtualRow.start}px)` }}
                      >
                        <button
                          className={`${fundStyles.option} ${supplier.id === selectedSupplierId ? 'bg-(--surface-secondary-lowest)' : ''}`}
                          onClick={() => handleSelect(supplier)}
                        >
                          <span className="body-md-semibold">{supplier.name}</span>
                          <div className={fundStyles.divider} />
                          <span className="body-sm-regular">{supplier.taxId}</span>
                          <span className="ms-auto">
                            <img src={chevron} alt="" className={fundStyles.chevron} />
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </Dropdown>
      <img
        id={tooltipId}
        src={tooltipInfo}
        alt="info"
        className={styles['tooltip-icon']}
        tabIndex={0}
      />
      <CustomTooltip targetId={tooltipId} placement="bottom">
        <div className="body-sm-regular" style={{ color: 'var(--font-body-1)' }}>
          {t('searchSupplierTooltip')}
        </div>
      </CustomTooltip>
    </div>
  );
};

export default SupplierFilter;
